import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { useApiContext, useGetBonuses, useWindowSize } from 'react-easyrocket';
import { Button } from 'src/components/Buttons/Button';
import { Logo } from './componets/Logo';
import { HeaderBurgerIcon } from 'src/components/svg/HeaderBurgerIcon';
import { Coins } from 'src/components/svg/Coins';
import { Gobelet } from '../svg/Gobelet';
import { AccountIcon } from 'src/components/svg/Accounts';
import { useModalContext } from 'src/hooks/useModalContext';
import Languages from 'src/modules/locales/components/Languages';
import { useRoute } from 'src/router/hooks';
import '../styles.css';
import { useGetCountOfTournaments } from 'src/hooks/useGetCountOfTournaments';
import { useDrawer } from 'src/hooks/useDrawer';

import { useGetBalance } from 'src/hooks/useGetBalance';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';

export const HeaderInner = React.memo(() => {
  const { iconWithBalance } = useGetBalance();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const { ROUTE } = useRoute();
  const { isAuthenticated } = useApiContext();
  const { t } = useTranslation();
  const location = useLocation();
  const { openLeftDrawer } = useDrawer();
  const isAccountLayout = location.pathname.includes(ROUTE.ACCOUNT.path);
  const { data: visibleBonuses } = useGetBonuses();
  const { is1024, is640 } = useWindowSize();
  const countOfTournaments = useGetCountOfTournaments();
  const { startRegistration, startLogin } = useAuthFlow();

  const handleLanguagesClick = useCallback(() => {
    menuRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  }, [menuRef]);

  const handleDepositClick = useCallback(() => {
    isAccountLayout ? navigate(ROUTE.ACCOUNT_DEPOSIT.get()) : openModal('DEPOSIT');
  }, [ROUTE.ACCOUNT_DEPOSIT, isAccountLayout, navigate, openModal]);

  return (
    <div className="header-inner sm:gap-0 gap-2">
      {!is1024 ? (
        <div className="flex flex-row justify-center items-center">
          <div
            className="flex-row justify-start lg:justify-center items-center cursor-pointer flex "
            onClick={openLeftDrawer}>
            <HeaderBurgerIcon className="!w-[28px] [&_path]:fill-n_burger_icon" />
          </div>
          <Logo />
        </div>
      ) : (
        <>
          <div className="flex flex-[2_1_0%] justify-start items-center gap-7 mr-1">
            <div className="p-2 flex flex-row justify-start items-center " onClick={openLeftDrawer}>
              <HeaderBurgerIcon className="[&_path]:fill-n_burger_icon" />
            </div>
            <p
              className={classNames(
                'hidden cursor-pointer lg:block uppercase text-h_link font-bold text-[20px] hover:text-gc_active_text',
                location.pathname.includes('/games') && !location.pathname.includes('/casino-live')
                  ? '!text-gc_active_text'
                  : ''
              )}
              onClick={() => {
                navigate(ROUTE.GAMES_SLOTS.get());
              }}>
              {t('header.games')}
            </p>
            <p
              className={classNames(
                'hidden cursor-pointer lg:block uppercase text-h_link font-bold text-[20px] hover:text-gc_active_text',
                location.pathname.includes('vip') && !location.pathname.includes('account')
                  ? '!text-gc_active_text'
                  : ''
              )}
              onClick={() => {
                navigate(ROUTE.VIP.get());
              }}>
              {t('header.vip')}
            </p>
            {visibleBonuses && visibleBonuses?.length !== 0 && (
              <div className="cursor-pointer flex flex-row items-center justify-start">
                <p
                  className={classNames(
                    'hidden lg:block uppercase text-h_link font-bold text-[20px] hover:text-gc_active_text',
                    location.pathname.includes('bonuses') ? '!text-gc_active_text' : ''
                  )}
                  onClick={() => {
                    navigate(ROUTE.BONUSES.get());
                  }}>
                  {t('header.bonuses')}
                </p>
                <div className="hidden self-center bg-vp_loyalty_arrow_bg rounded-full ml-[5px] lg:flex items-center justify-center w-[14px] h-[14px]">
                  <p className="pt-[1px] w-full text-center text-[7px] font-bold text-text_color_bage">
                    {visibleBonuses?.length}
                  </p>
                </div>
              </div>
            )}
            <div className="cursor-pointer flex flex-row items-center justify-start">
              <Gobelet
                className="hidden cursor-pointer lg:block"
                onClick={() => {
                  navigate(ROUTE.TOURNAMENTS.get());
                }}
              />
              {Boolean(countOfTournaments) && (
                <div className="hidden self-center bg-vp_loyalty_arrow_bg rounded-full ml-[5px] lg:flex items-center justify-center w-[14px] h-[14px]">
                  <p className="pt-[1px] w-full text-center text-[7px] font-bold text-text_color_bage">
                    {countOfTournaments}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-center md:justify-start items-center">
            <Logo />
          </div>
        </>
      )}
      {!isAuthenticated && (
        <div className="flex-[2_1_0%] flex flex-row items-center justify-end gap-x-2.5">
          {is640 && (
            <Button
              title={`${t('general.logIn')}`}
              label={`${t('general.logIn')}`}
              mode="button-secondary"
              className="container-with-wide-content-header px-5 w-auto"
              onClick={() => startLogin()}></Button>
          )}
          <Button
            mode="button-primary"
            title={`${t('general.signUp')}`}
            label={`${t('general.signUp')}`}
            onClick={() => startRegistration()}
            className="container-with-wide-content-header px-5 w-auto"></Button>
          <Languages
            handleClick={handleLanguagesClick}
            additionalContainerClassName={'hidden xl:block'}
          />
        </div>
      )}
      {isAuthenticated && (
        <div className="flex-[2_1_0%] flex flex-row items-center justify-end gap-x-2.5">
          <div
            onClick={handleDepositClick}
            className="flex flex-row items-center justify-center gap-2.5 cursor-pointer py-[5px] px-[24px] border-[2px] border-solid rounded-[50px] border-b_simple_text">
            <p className="text-b_simple_text text-[15px] font-bold">{iconWithBalance}</p>
            {is1024 && <Coins className="w-[30px] h-[30px]" />}
          </div>
          <div
            onClick={() => navigate(ROUTE.ACCOUNT_PROFILE_MAIN.get())}
            className="cursor-pointer">
            <AccountIcon className="hover:!text-b_simple_text" />
          </div>
          <Languages
            handleClick={handleLanguagesClick}
            additionalContainerClassName={'hidden xl:block'}
          />
        </div>
      )}
    </div>
  );
});

HeaderInner.displayName = 'HeaderInner';
